import React from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../Context/TenantContext";
import { FaUserShield, FaUserGraduate } from "react-icons/fa";
import { HiChevronRight } from "react-icons/hi";
import TenantLoginFooter from "./TenantLoginFooter";

/**
 * TenantPortalHome
 *
 * Landing page shown when a user visits a tenant subdomain
 * (e.g. https://svecw.theskillgenie.com).
 * Presents two entry points: Admin Login and Student Login.
 */
const TenantPortalHome = () => {
  const navigate = useNavigate();
  const { getCollegeName } = useTenant();

  const collegeName = getCollegeName();

  return (
    <div className="min-h-screen bg-bgMain text-body flex flex-col items-center justify-between relative overflow-hidden font-body">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.7s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.7s ease-out forwards; }
        .animate-slideDown { animation: slideDown 0.7s ease-out forwards; }
        .delay-150 { animation-delay: 150ms; }
        .delay-300 { animation-delay: 300ms; }
      `}</style>
      
      {/* Premium Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/5 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '10s' }}></div>
        <div className="absolute top-[20%] right-[15%] w-[150px] h-[150px] bg-accent/10 rounded-full blur-[60px]"></div>
        <div className="absolute bottom-[20%] left-[10%] w-[200px] h-[200px] bg-primary/10 rounded-full blur-[80px]"></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(#5B21B6 0.5px, transparent 0.5px)', backgroundSize: '24px 24px' }}></div>
      </div>

      {/* Main Content Area */}
      <main className="flex-grow flex flex-col items-center w-full z-10 pt-10 pb-8 px-4">

        {/* Hero Section */}
        <div className="flex flex-col items-center mb-6 text-center max-w-3xl opacity-0 animate-slideDown">
          <h1 className="text-[22px] sm:text-[28px] lg:text-[34px] font-[800] text-heading leading-tight mb-2">
            {collegeName}
          </h1>
          <p className="text-muted text-sm font-medium flex items-center gap-1.5 opacity-0 animate-slideDown delay-150">
            Powered by <span className="text-primary font-semibold">The Skill Genie</span>
          </p>
        </div>

        {/* Login Selection Card */}
        <div
          className="w-full max-w-[480px] px-8 py-8 bg-white/80 backdrop-blur-xl border border-white rounded-[22px] shadow-soft text-center opacity-0 animate-slideUp delay-300"
          style={{ boxShadow: "0 16px 40px rgba(0,0,0,0.06)" }}
        >
          <h2 className="text-[22px] font-bold text-heading mb-1.5">Welcome</h2>
          <p className="text-muted text-sm mb-6">
            Please select how you would like to sign in.
          </p>

          <div className="space-y-3">
            {/* Admin Login Card */}
            <div
              onClick={() => navigate("/admin/login")}
              className="flex items-center justify-between px-5 h-[68px] border border-accentGray/50 rounded-[14px] w-full cursor-pointer bg-white hover:border-primary hover:shadow-hover hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <div className="flex items-center gap-4">
                <div className="w-[42px] h-[42px] flex items-center justify-center rounded-[11px] bg-primary/10 text-primary text-lg group-hover:bg-primary group-hover:text-white transition-all duration-200">
                  <FaUserShield />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-heading group-hover:text-primary transition-colors">
                    Admin Login
                  </p>
                  <p className="text-xs text-muted">
                    For institution administrators
                  </p>
                </div>
              </div>
              <div className="text-muted group-hover:text-primary group-hover:translate-x-1 transition-all duration-200">
                <HiChevronRight size={20} />
              </div>
            </div>

            {/* Student Login Card */}
            <div
              onClick={() => navigate("/student/login")}
              className="flex items-center justify-between px-5 h-[68px] border border-accentGray/50 rounded-[14px] w-full cursor-pointer bg-white hover:border-primary hover:shadow-hover hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <div className="flex items-center gap-4">
                <div className="w-[42px] h-[42px] flex items-center justify-center rounded-[11px] bg-primary/10 text-primary text-lg group-hover:bg-primary group-hover:text-white transition-all duration-200">
                  <FaUserGraduate />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-heading group-hover:text-primary transition-colors">
                    Student Login
                  </p>
                  <p className="text-xs text-muted">
                    For enrolled students
                  </p>
                </div>
              </div>
              <div className="text-muted group-hover:text-primary group-hover:translate-x-1 transition-all duration-200">
                <HiChevronRight size={20} />
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-muted">
            Not your institution?{" "}
            <a
              href="https://theskillgenie.com"
              className="text-primary hover:underline font-semibold"
            >
              Go to The Skill Genie
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <div className="w-full z-10">
        <TenantLoginFooter />
      </div>
    </div>
  );
};

export default TenantPortalHome;